<template>
  <form @submit.prevent="submit">
    <div class="row g-3">
      <div class="col-md-4">
        <label class="form-label">{{ t('settings.nationalities.code') }}</label>
        <input
          class="form-control borderless-input"
          v-model.trim="data.code"
          maxlength="3"
          :class="{ 'is-invalid': errors.code }"
          required
        />
        <div class="invalid-feedback" v-if="errors.code">{{ errors.code }}</div>
      </div>

      <div class="col-md-4">
        <label class="form-label">{{ t('settings.nationalities.nameAr') }}</label>
        <input
          class="form-control borderless-input"
          v-model.trim="data.name_translations.ar"
          :class="{ 'is-invalid': errors['name_translations.ar'] }"
          required
        />
        <div class="invalid-feedback" v-if="errors['name_translations.ar']">
          {{ errors['name_translations.ar'] }}
        </div>
      </div>

      <div class="col-md-4">
        <label class="form-label">{{ t('settings.nationalities.nameEn') }}</label>
        <input
          class="form-control borderless-input"
          v-model.trim="data.name_translations.en"
          :class="{ 'is-invalid': errors['name_translations.en'] }"
          required
        />
        <div class="invalid-feedback" v-if="errors['name_translations.en']">
          {{ errors['name_translations.en'] }}
        </div>
      </div>
    </div>
  </form>
</template>

<script setup>
import { reactive, watch, defineProps, defineEmits, defineExpose } from 'vue'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({
      code: '',
      name_translations: { ar: '', en: '' },
      name: '' // legacy mirror of Arabic
    })
  }
})

const emit = defineEmits(['submit', 'validation-error', 'update:form-data'])

/** Guarantee stable shape + put Arabic into legacy name if needed */
const normalize = (src = {}) => {
  const code = src.code ?? ''
  const nt = src.name_translations ?? {}
  const ar = nt.ar ?? src.name ?? ''
  const en = nt.en ?? ''
  const name = src.name ?? ar
  return { code, name_translations: { ar, en }, name }
}

const data = reactive(normalize(props.initialData))
const errors = reactive({})

/** Keep the same reactive objects; only assign fields */
watch(
  () => props.initialData,
  (v) => {
    const n = normalize(v || {})
    data.code = n.code
    data.name = n.name
    data.name_translations.ar = n.name_translations.ar
    data.name_translations.en = n.name_translations.en
  },
  { immediate: true, deep: false }
)

watch(
  data,
  (v) => emit('update:form-data', JSON.parse(JSON.stringify(v))),
  { deep: true }
)

const validate = () => {
  Object.keys(errors).forEach(k => delete errors[k])
  let ok = true
  if (!data.code?.trim()) { errors.code = t('validation.required'); ok = false }
  if (!data.name_translations.ar?.trim()) { errors['name_translations.ar'] = t('validation.required'); ok = false }
  if (!data.name_translations.en?.trim()) { errors['name_translations.en'] = t('validation.required'); ok = false }
  return ok
}

const submit = () => {
  if (!validate()) return emit('validation-error')
  // keep legacy `name` synced to Arabic
  data.name = data.name_translations.ar || ''
  emit('submit', JSON.parse(JSON.stringify(data)))
}

const setServerErrors = (server) => {
  Object.keys(errors).forEach(k => delete errors[k])
  if (!server) return
  for (const k in server) errors[k] = Array.isArray(server[k]) ? server[k][0] : server[k]
}

defineExpose({ submit, setServerErrors })
</script>
